import { useEffect, useState } from 'react';
import { CompanyRolesDropdownOptions } from '~/constants/company-roles';
import { useUpdateUserCompanySettings } from '~/mutations/team/useUpdateUserCompanySettings';
import { useTeamMembersQuery } from '~/requests/team';
import toast from 'react-hot-toast';
import Modal from '~/components/Modal';
import { Button, Table } from '~/components';
import { storage } from '~/utils';
import { logout } from '~/api/auth';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { ToastDuration } from '~/constants/toasts';
import { useUserContext } from '~/hooks';

const CompanyRoleTableCell = ({
  member,
  onUpdated,
}: {
  member: { id: string; companyRoleCode: string; isTeamInvite?: boolean; isUser?: boolean; active?: boolean };
  onUpdated?: () => void;
}) => {
  const { loggedInUser } = useUserContext();
  const { permissions, refetch: refetchPermissions } = usePermissions();
  const [companyRoleCode, setCompanyRoleCode] = useState(member?.companyRoleCode);
  const { mutateAsync: updateUserCompanySettings, status: updateUserCompanySettingsStatus } =
    useUpdateUserCompanySettings();
  const { data: teamMembers } = useTeamMembersQuery();
  const [desiredSelfRole, setDesiredSelfRole] = useState<null | 'ADMIN' | 'STANDARD_USER'>(null);
  const isSelf = member.id === loggedInUser.id;

  useEffect(() => {
    setCompanyRoleCode(member.companyRoleCode);
  }, [member.companyRoleCode]);

  const canEdit = (permissions?.isCompanyAdmin || permissions?.isAdmin) && member.active !== false;
  const isBusy = updateUserCompanySettingsStatus === 'loading';

  const handleChange = (companyRole: 'ADMIN' | 'STANDARD_USER') => {
    if (isSelf && !permissions?.isAdmin && teamMembers?.filter((m: any) => m?.companyRoleCode === 'ADMIN' && m.isUser).length === 1 && companyRole !== 'ADMIN') {
      toast.error(
        'You cannot remove your Admin privileges when there are no other Admins in your organization. Please set another user as an Admin and try again.',
        { duration: ToastDuration.LONG }
      );
      return;
    }
    if (isSelf) {
      setDesiredSelfRole(companyRole);
      return;
    }
    setCompanyRoleCode(companyRole);
    const payload = member.isTeamInvite ? { inviteId: member.id, companyRole } : { userId: member.id, companyRole };
    updateUserCompanySettings(payload)
      .then(() => onUpdated?.())
      .catch(() => setCompanyRoleCode(member?.companyRoleCode));
  };

  return (
    <>
      <Table.Cell
        isEditable
        textAlign="left"
        style={{ minWidth: '9rem' }}
        value={companyRoleCode}
        defaultValue="—"
        options={CompanyRolesDropdownOptions}
        disabled={isBusy || !canEdit}
        onChange={(value) => handleChange(value as 'ADMIN' | 'STANDARD_USER')}
      />
      {desiredSelfRole && (
        <Modal closeModal={() => setDesiredSelfRole(null)}>
          <Modal.Title>Changing your privileges</Modal.Title>
          <Modal.Content>
            <p>
              By changing your user role from Admin, you will lose the ability to set default project leads and to
              change the roles of your colleagues. Additionally, you will be unable to re-assign yourself the Admin role
              without assistance.
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setDesiredSelfRole(null)}>
              Cancel
            </Button>
            <Button
              primary
              disabled={updateUserCompanySettingsStatus === 'loading'}
              loading={updateUserCompanySettingsStatus === 'loading'}
              onClick={() => {
                updateUserCompanySettings({ userId: member.id, companyRole: desiredSelfRole })
                  .then(async () => {
                    onUpdated?.();
                    try {
                      await refetchPermissions();
                      setCompanyRoleCode(desiredSelfRole);
                      setDesiredSelfRole(null);
                      toast.success('You successfully changed your own role.');
                    } catch (err) {
                      toast.success('You successfully changed your own role. You will now be redirected to login...');
                      setTimeout(() => {
                        logout().finally(() => {
                          storage.clear();
                          window.location.href = '/login';
                        });
                      }, 1500);
                    }
                  })
                  .catch((err: any) => {
                    console.error(err);
                    toast.error('Error occurred changing your own role. Please try again.');
                  });
              }}
            >
              I understand
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default CompanyRoleTableCell;
